import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Spinner, Badge } from 'react-bootstrap';
import {
  Chart as ChartJS, BarElement, LineElement, PointElement,
  CategoryScale, LinearScale, Filler, Tooltip as ChartTooltip,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import {
  useGetMarketingActivitySummaryQuery,
  useGetMarketingActivityFirmsQuery,
  useGetMarketingActivityMembersQuery,
  useGetMarketingActivityItemsQuery,
  useLazyGetMarketingActivityCsvQuery,
} from '../../app/api';
import { brand, cardStyle, headStyle, AdminPage } from '../entrepreneur/common';

/* Marketing → Activity. Who is actually opening the collateral.
 *
 * Every open of every item — by a B2B member in their hub, or by PCA staff on
 * the internal read-only view — writes a marketing_events row. This page reads
 * them back. See docs/marketing-resources.md and api/app/db/marketing_activity.py.
 *
 * TWO SWITCHES CORRECT THE SAME ERROR and both default to off: counting PCA as
 * if it were a customer. "PCA staff views" folds in our own browsing of the
 * internal view; "log-in-as sessions" folds in clicks made while an admin was
 * impersonating a member to support them. Both are recorded either way — only
 * the default reading is opinionated — so either can be switched back on here
 * without losing history.
 *
 * WHAT THIS PAGE CANNOT TELL YOU: it counts opens, not reads. An open is the
 * moment a member asked us for the file or the Box link, which is the last
 * event we can see; whether they then read the deck happens on Box's side.
 */

ChartJS.register(BarElement, LineElement, PointElement, CategoryScale, LinearScale,
  Filler, ChartTooltip);

// One mark colour, used for both charts. Each chart is a single series, so
// there is no categorical palette to get wrong and no legend to carry — the
// title names the series. #0069a6 is the same mark blue as the Net New AUM
// chart; validated against the light chart surface (lightness band, chroma
// floor and contrast all pass). Goldenrod is deliberately NOT a data colour: it
// fails contrast against a white surface at 2.43:1 and stays an accent for
// banners and rules.
const MARK = '#0069a6';
const MARK_SOFT = 'rgba(0,105,166,0.12)';
const GRIDLINE = '#eef1f5';
const AXIS_INK = '#8a8f98';

const RANGES = [
  { key: '30', label: '30 days', days: 30 },
  { key: '90', label: '90 days', days: 90 },
  { key: '365', label: '12 months', days: 365 },
];

const iso = (d) => d.toISOString().slice(0, 10);

const rangeDates = (days) => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (days - 1));
  return { start: iso(start), end: iso(end) };
};

const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—'
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

// Axis labels for a daily series get unreadable past a few weeks, so long
// windows are bucketed by month server-side and labelled accordingly.
const fmtBucket = (key, bucket) => {
  const d = new Date(`${key}T00:00:00`);
  if (Number.isNaN(d.getTime())) return key;
  return bucket === 'month'
    ? d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' })
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const controlStyle = {
  border: `1px solid ${brand.softGray}`, borderRadius: 6, padding: '5px 10px',
  fontSize: '0.8rem', color: brand.deepBlue, background: 'white',
};
const btnTiny = {
  background: 'white', color: brand.charcoal, border: `1px solid ${brand.softGray}`,
  borderRadius: 6, padding: '4px 10px', fontWeight: 600, fontSize: '0.76rem', cursor: 'pointer',
};

/* A headline number. Not a chart — four single values have no shape to show,
 * and a bar chart of four unrelated measures would invite comparing them. */
function Stat({ label, value, hint }) {
  return (
    <div style={{ ...cardStyle, background: 'white', padding: '0.9rem 1.1rem', flex: 1, minWidth: 150 }}>
      <div style={{ color: brand.charcoal, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: 700 }}>
        {label}
      </div>
      <div style={{ color: brand.deepBlue, fontSize: '1.75rem', fontWeight: 800, lineHeight: 1.2 }}>
        {value?.toLocaleString() ?? '—'}
      </div>
      {hint && <div style={{ color: brand.charcoal, fontSize: '0.74rem' }}>{hint}</div>}
    </div>
  );
}
Stat.propTypes = { label: PropTypes.string, value: PropTypes.number, hint: PropTypes.string };

/* Opens over time. One series, so no legend; a filled line rather than bars
 * because the question is the shape of the trend, not the value of any one day.
 * Points are hidden until hover — 90 of them would be noise — and the tooltip
 * follows the whole index rather than requiring a hit on the mark itself. */
function TrendChart({ series, bucket }) {
  const data = useMemo(() => ({
    labels: series.map((p) => fmtBucket(p.date, bucket)),
    datasets: [{
      data: series.map((p) => p.opens),
      borderColor: MARK,
      backgroundColor: MARK_SOFT,
      borderWidth: 2,
      fill: true,
      tension: 0.25,
      pointRadius: 0,
      pointHoverRadius: 5,          // 10px target, comfortably over the 8px floor
      pointHoverBackgroundColor: MARK,
      pointHoverBorderColor: 'white',
      pointHoverBorderWidth: 2,     // 2px surface ring, so the point reads on the fill
    }],
  }), [series, bucket]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => items[0]?.label ?? '',
          label: (item) => `${item.parsed.y.toLocaleString()} open${item.parsed.y === 1 ? '' : 's'}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: AXIS_INK, font: { size: 11 }, maxRotation: 0, autoSkip: true,
          maxTicksLimit: 8,
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: GRIDLINE, drawTicks: false },
        border: { display: false },
        ticks: { color: AXIS_INK, font: { size: 11 }, precision: 0, maxTicksLimit: 5 },
      },
    },
  }), []);

  return <div style={{ height: 220 }}><Line data={data} options={options} /></div>;
}
TrendChart.propTypes = { series: PropTypes.array, bucket: PropTypes.string };

/* Opens per section. Horizontal because the section names are long, and four
 * bars in a fixed order — the value is the length, so the fill is one flat
 * colour rather than a meaningless hue per row. Each bar is directly labelled
 * (there are only four), which is also the relief that keeps the chart readable
 * without relying on colour. */
function SectionChart({ sections }) {
  const rows = useMemo(() => sections.filter((s) => s.opens > 0), [sections]);

  const data = useMemo(() => ({
    labels: rows.map((s) => s.category_name),
    datasets: [{
      data: rows.map((s) => s.opens),
      backgroundColor: MARK,
      borderRadius: 4,          // rounded data-end...
      borderSkipped: 'start',   // ...square where it meets the baseline
      barThickness: 18,
    }],
  }), [rows]);

  const max = Math.max(1, ...rows.map((s) => s.opens));

  const options = useMemo(() => ({
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 40 } },   // room for the direct labels
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (item) => {
            const row = rows[item.dataIndex];
            return `${item.parsed.x.toLocaleString()} opens · ${row.firms} firm${row.firms === 1 ? '' : 's'}`;
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        suggestedMax: max * 1.15,
        grid: { color: GRIDLINE, drawTicks: false },
        border: { display: false },
        ticks: { color: AXIS_INK, font: { size: 11 }, precision: 0, maxTicksLimit: 5 },
      },
      y: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: brand.charcoal, font: { size: 11 } },
      },
    },
  }), [rows, max]);

  // Values drawn beside the bar ends rather than inside them: inside would need
  // white-on-blue text, which is the series colour doing a text job.
  const labelPlugin = useMemo(() => ({
    id: 'sectionValueLabels',
    afterDatasetsDraw(chart) {
      const { ctx } = chart;
      const meta = chart.getDatasetMeta(0);
      ctx.save();
      ctx.fillStyle = brand.charcoal;
      ctx.font = '600 11px system-ui, sans-serif';
      ctx.textBaseline = 'middle';
      meta.data.forEach((bar, i) => {
        ctx.fillText(rows[i].opens.toLocaleString(), bar.x + 8, bar.y);
      });
      ctx.restore();
    },
  }), [rows]);

  if (!rows.length) {
    return <div style={{ color: brand.charcoal, fontSize: '0.85rem' }}>No opens in this window.</div>;
  }
  return (
    <div style={{ height: Math.max(120, rows.length * 42) }}>
      <Bar data={data} options={options} plugins={[labelPlugin]} />
    </div>
  );
}
SectionChart.propTypes = { sections: PropTypes.array };

/* One firm, expandable into its members. The member list is fetched only when
 * the row is opened — a page of forty firms shouldn't pull forty member lists
 * nobody asked to see. */
function FirmRow({ firm, params, maxOpens }) {
  const [open, setOpen] = useState(false);
  const { data, isFetching } = useGetMarketingActivityMembersQuery(
    { firm_id: firm.firm_id, params },
    { skip: !open || firm.firm_id == null },
  );
  const members = data?.members || [];
  const isEmployeeBucket = firm.firm_id == null;

  return (
    <>
      <tr style={{ borderTop: `1px solid ${brand.softGray}` }}>
        <td style={{ padding: '0.5rem 0.6rem' }}>
          {isEmployeeBucket ? (
            <span style={{ fontWeight: 600, color: brand.charcoal }}>
              {firm.firm_name} <Badge bg="secondary" style={{ fontWeight: 600 }}>internal</Badge>
            </span>
          ) : (
            <button type="button" style={{ ...btnTiny, border: 'none', background: 'transparent', padding: 0, fontWeight: 700, color: brand.deepBlue }}
              onClick={() => setOpen((v) => !v)} aria-expanded={open}>
              <i className={`bi bi-chevron-${open ? 'down' : 'right'} me-1`} />
              {firm.firm_name}
            </button>
          )}
        </td>
        <td style={{ padding: '0.5rem 0.6rem', width: '38%' }}>
          {/* A bar in the row rather than a separate chart: forty firms is a
              table, and the length still gives the ranking at a glance. */}
          <div style={{ background: GRIDLINE, borderRadius: 4, height: 8 }}>
            <div style={{
              width: `${Math.max(2, (firm.opens / maxOpens) * 100)}%`,
              background: MARK, borderRadius: 4, height: 8,
            }} />
          </div>
        </td>
        <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', fontWeight: 700, color: brand.deepBlue }}>
          {firm.opens.toLocaleString()}
        </td>
        <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', color: brand.charcoal }}>{firm.people}</td>
        <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', color: brand.charcoal, whiteSpace: 'nowrap' }}>
          {fmtDateTime(firm.last_open)}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} style={{ padding: '0 0.6rem 0.6rem 2rem', background: '#fbfaf8' }}>
            {isFetching && <Spinner size="sm" />}
            {!isFetching && members.length === 0 && (
              <div style={{ color: brand.charcoal, fontSize: '0.8rem', padding: '0.4rem 0' }}>
                No member activity in this window.
              </div>
            )}
            {members.map((m) => (
              <div key={m.actor_uuid} style={{ display: 'flex', gap: '0.75rem', padding: '0.25rem 0', fontSize: '0.82rem' }}>
                <span style={{ flex: 1, color: brand.deepBlue }}>{m.name}</span>
                <span style={{ color: brand.charcoal }}>{m.opens.toLocaleString()} opens</span>
                <span style={{ color: brand.charcoal, minWidth: 110, textAlign: 'right' }}>{fmtDateTime(m.last_open)}</span>
              </div>
            ))}
          </td>
        </tr>
      )}
    </>
  );
}
FirmRow.propTypes = { firm: PropTypes.object, params: PropTypes.object, maxOpens: PropTypes.number };

export default function MarketingActivity() {
  const [rangeKey, setRangeKey] = useState('90');
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [categoryId, setCategoryId] = useState('');
  const [includeEmployees, setIncludeEmployees] = useState(false);
  const [includeImpersonated, setIncludeImpersonated] = useState(false);
  const [csvError, setCsvError] = useState(null);

  const [fetchCsv, csvState] = useLazyGetMarketingActivityCsvQuery();

  const window_ = useMemo(() => {
    if (rangeKey === 'custom' && custom.start && custom.end) return custom;
    const preset = RANGES.find((r) => r.key === rangeKey) || RANGES[1];
    return rangeDates(preset.days);
  }, [rangeKey, custom]);

  // A daily series over a year is 365 unreadable ticks, so long windows roll up
  // to months. Decided here rather than server-side so the choice is visible
  // next to the chart it governs.
  const bucket = rangeKey === '365' ? 'month' : 'day';

  const params = useMemo(() => ({
    start: window_.start,
    end: window_.end,
    ...(categoryId ? { category_id: Number(categoryId) } : {}),
    ...(includeEmployees ? { include_employees: true } : {}),
    ...(includeImpersonated ? { include_impersonated: true } : {}),
  }), [window_, categoryId, includeEmployees, includeImpersonated]);

  const summary = useGetMarketingActivitySummaryQuery({ ...params, bucket });
  const firms = useGetMarketingActivityFirmsQuery(params);
  const items = useGetMarketingActivityItemsQuery(params);

  const totals = summary.data?.totals;
  const sections = summary.data?.sections || [];
  const series = summary.data?.series || [];
  const categories = summary.data?.categories || [];
  const firmRows = firms.data?.firms || [];
  const maxFirmOpens = Math.max(1, ...firmRows.map((f) => f.opens));
  const topItems = items.data?.items || [];
  const neverOpened = items.data?.never_opened || [];

  const downloadCsv = async () => {
    setCsvError(null);
    const res = await fetchCsv(params);
    if (res.error || !res.data) { setCsvError('Could not build the download.'); return; }
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `marketing-activity-${window_.start}-to-${window_.end}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Released on the next tick — revoking synchronously can cancel the
    // download in Safari before it has read the blob.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const loading = summary.isLoading || firms.isLoading;

  return (
    <AdminPage
      section="Marketing"
      icon="megaphone"
      title="Activity"
      subtitle="Which firms and members are opening the collateral."
      actions={
        <button type="button" style={{ ...btnTiny, padding: '6px 14px', fontSize: '0.82rem' }}
          onClick={downloadCsv} disabled={csvState.isFetching}>
          <i className="bi bi-download me-1" />
          {csvState.isFetching ? 'Building…' : 'Export CSV'}
        </button>
      }
    >
      {csvError && (
        <div style={{ background: 'rgba(192,57,43,0.08)', border: `1px solid ${brand.red}`, color: brand.red, borderRadius: 8, padding: '0.6rem 0.9rem', marginBottom: '1rem', fontSize: '0.85rem' }}>
          <i className="bi bi-exclamation-triangle me-1" />{csvError}
        </div>
      )}

      {/* Filters in one row above the charts, so changing the window is
          obviously the same act for every panel below. */}
      <Card style={{ ...cardStyle, marginBottom: '1rem' }}>
        <Card.Body style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', padding: '0.75rem 1rem' }}>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            {RANGES.map((r) => (
              <button key={r.key} type="button"
                onClick={() => setRangeKey(r.key)}
                style={{
                  ...btnTiny,
                  background: rangeKey === r.key ? brand.deepBlue : 'white',
                  color: rangeKey === r.key ? 'white' : brand.charcoal,
                  borderColor: rangeKey === r.key ? brand.deepBlue : brand.softGray,
                }}>
                {r.label}
              </button>
            ))}
            <button type="button" onClick={() => setRangeKey('custom')}
              style={{
                ...btnTiny,
                background: rangeKey === 'custom' ? brand.deepBlue : 'white',
                color: rangeKey === 'custom' ? 'white' : brand.charcoal,
                borderColor: rangeKey === 'custom' ? brand.deepBlue : brand.softGray,
              }}>
              Custom
            </button>
          </div>

          {rangeKey === 'custom' && (
            <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
              <input type="date" style={controlStyle} value={custom.start}
                onChange={(e) => setCustom({ ...custom, start: e.target.value })} aria-label="Start date" />
              <span style={{ color: brand.charcoal, fontSize: '0.8rem' }}>to</span>
              <input type="date" style={controlStyle} value={custom.end}
                onChange={(e) => setCustom({ ...custom, end: e.target.value })} aria-label="End date" />
            </div>
          )}

          <select style={controlStyle} value={categoryId} aria-label="Section"
            onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">All sections</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: brand.charcoal, fontSize: '0.8rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={includeEmployees}
                onChange={(e) => setIncludeEmployees(e.target.checked)} />
              Include PCA staff views
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: brand.charcoal, fontSize: '0.8rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={includeImpersonated}
                onChange={(e) => setIncludeImpersonated(e.target.checked)} />
              Include log-in-as sessions
            </label>
          </div>
        </Card.Body>
      </Card>

      {loading && <div className="text-center py-4"><Spinner /></div>}

      {!loading && (
        <>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <Stat label="Opens" value={totals?.opens} hint={`${window_.start} → ${window_.end}`} />
            <Stat label="Firms" value={totals?.firms} hint="with at least one open" />
            <Stat label="People" value={totals?.people} hint="distinct members" />
            <Stat label="Items opened" value={totals?.items} hint="distinct pieces" />
          </div>

          <div className="row g-3" style={{ marginBottom: '1rem' }}>
            <div className="col-lg-7">
              <Card style={{ ...cardStyle, height: '100%' }}>
                <Card.Header style={headStyle}>Opens over time</Card.Header>
                <Card.Body>
                  {series.length === 0
                    ? <div style={{ color: brand.charcoal, fontSize: '0.85rem' }}>No opens in this window.</div>
                    : <TrendChart series={series} bucket={bucket} />}
                </Card.Body>
              </Card>
            </div>
            <div className="col-lg-5">
              <Card style={{ ...cardStyle, height: '100%' }}>
                <Card.Header style={headStyle}>Opens by section</Card.Header>
                <Card.Body><SectionChart sections={sections} /></Card.Body>
              </Card>
            </div>
          </div>

          <Card style={{ ...cardStyle, marginBottom: '1rem' }}>
            <Card.Header style={headStyle}>
              By firm
              <span style={{ fontWeight: 400, color: brand.charcoal, fontSize: '0.78rem', marginLeft: '0.5rem' }}>
                — click a firm to see its members
              </span>
            </Card.Header>
            <Card.Body style={{ padding: 0 }}>
              {firmRows.length === 0 ? (
                <div style={{ padding: '1rem', color: brand.charcoal, fontSize: '0.85rem' }}>
                  No firm activity in this window.
                </div>
              ) : (
                <table style={{ width: '100%', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ color: brand.charcoal, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                      <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left' }}>Firm</th>
                      <th aria-label="Relative volume" />
                      <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Opens</th>
                      <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>People</th>
                      <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Last open</th>
                    </tr>
                  </thead>
                  <tbody>
                    {firmRows.map((f) => (
                      <FirmRow key={f.firm_id ?? 'internal'} firm={f} params={params} maxOpens={maxFirmOpens} />
                    ))}
                  </tbody>
                </table>
              )}
            </Card.Body>
          </Card>

          <div className="row g-3">
            <div className="col-lg-7">
              <Card style={{ ...cardStyle, height: '100%' }}>
                <Card.Header style={headStyle}>Most opened</Card.Header>
                <Card.Body style={{ padding: 0 }}>
                  {topItems.length === 0 ? (
                    <div style={{ padding: '1rem', color: brand.charcoal, fontSize: '0.85rem' }}>Nothing opened yet.</div>
                  ) : (
                    <table style={{ width: '100%', fontSize: '0.85rem' }}>
                      <tbody>
                        {topItems.map((it) => (
                          <tr key={`${it.target_type}-${it.target_id}`} style={{ borderTop: `1px solid ${brand.softGray}` }}>
                            <td style={{ padding: '0.45rem 0.6rem' }}>
                              <div style={{ color: brand.deepBlue, fontWeight: 600 }}>{it.title}</div>
                              <div style={{ color: brand.charcoal, fontSize: '0.74rem' }}>
                                {it.category_name}
                                {it.target_type === 'GROUP' && ' · section heading'}
                              </div>
                            </td>
                            <td style={{ padding: '0.45rem 0.6rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                              <span style={{ fontWeight: 700, color: brand.deepBlue }}>{it.opens.toLocaleString()}</span>
                              <span style={{ color: brand.charcoal, fontSize: '0.74rem' }}> · {it.firms} firm{it.firms === 1 ? '' : 's'}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </Card.Body>
              </Card>
            </div>
            <div className="col-lg-5">
              {/* The more actionable half of the leaderboard: what nobody
                  touched is invisible on a "top items" list by construction. */}
              <Card style={{ ...cardStyle, height: '100%' }}>
                <Card.Header style={headStyle}>
                  Not opened by any firm
                  <span style={{ fontWeight: 400, color: brand.charcoal, fontSize: '0.78rem', marginLeft: '0.5rem' }}>
                    — in this window
                  </span>
                </Card.Header>
                <Card.Body style={{ padding: 0, maxHeight: 420, overflowY: 'auto' }}>
                  {neverOpened.length === 0 ? (
                    <div style={{ padding: '1rem', color: brand.charcoal, fontSize: '0.85rem' }}>
                      Every published item was opened at least once. That&apos;s unusual — worth a look at the window.
                    </div>
                  ) : neverOpened.map((it) => (
                    <div key={it.id} style={{ padding: '0.45rem 0.6rem', borderTop: `1px solid ${brand.softGray}` }}>
                      <div style={{ color: brand.deepBlue, fontSize: '0.85rem' }}>{it.title}</div>
                      <div style={{ color: brand.charcoal, fontSize: '0.74rem' }}>{it.category_name}</div>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </div>
          </div>

          <p style={{ color: brand.charcoal, fontSize: '0.75rem', marginTop: '1rem' }}>
            <i className="bi bi-info-circle me-1" />
            An &quot;open&quot; is the moment someone asked for a file or a link 
          </p>
        </>
      )}
    </AdminPage>
  );
}
